import React, { useState, useEffect } from 'react';

interface PWASuggestionProps {
  className?: string;
  showInProfile?: boolean;
}

export const PWASuggestion: React.FC<PWASuggestionProps> = ({ 
  className = '', 
  showInProfile = false 
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isInStandalone, setIsInStandalone] = useState(false);

  useEffect(() => {
    // Detectar si ya está en modo standalone (PWA)
    const checkStandalone = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                        (window.navigator as any).standalone === true;
      setIsInStandalone(standalone);
    };

    checkStandalone();
    window.addEventListener('resize', checkStandalone);

    // Verificar si ya fue descartada anteriormente
    const dismissed = localStorage.getItem('pwa-suggestion-dismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
      setIsVisible(false);
    }

    return () => {
      window.removeEventListener('resize', checkStandalone);
    };
  }, []);

  // No mostrar si ya está en PWA o no es visible
  if (isInStandalone || !isVisible || isDismissed) {
    return null;
  }

  const handleInstall = () => {
    // Mostrar instrucciones de instalación
    alert('Para instalar la app:\n\n1. Toca el botón de compartir (cuadrado con flecha)\n2. Selecciona "Añadir a Pantalla de Inicio"\n3. Toca "Añadir"');
    setIsVisible(false);
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    setIsVisible(false);
    
    // Guardar en localStorage para no mostrar de nuevo
    localStorage.setItem('pwa-suggestion-dismissed', 'true');
  };

  const handleLearnMore = () => {
    // Navegar a página de información PWA
    window.location.href = '/pwa-info';
  };

  return (
    <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 shadow-sm ${className}`}>
      <div className="flex flex-col sm:flex-row sm:items-start gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-blue-900">
              {showInProfile ? '¿Usando Servicio a Tiempo?' : 'Mejora tu experiencia'}
            </h3>
            <p className="text-sm text-blue-700 mt-1">
              {showInProfile 
                ? 'Instala la app en tu dispositivo para acceder más rápido, recibir notificaciones y usar offline.'
                : 'Instala Servicio a Tiempo como app para una experiencia más rápida y confiable.'
              }
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 sm:flex-shrink-0 sm:ml-auto">
          <button
            onClick={handleInstall}
            className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Instalar
          </button>
          <button
            onClick={handleLearnMore}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium focus:outline-none transition-colors"
          >
            Más info
          </button>
          <button
            onClick={handleDismiss}
            className="text-blue-400 hover:text-blue-600 focus:outline-none transition-colors"
            aria-label="Cerrar"
            title="Cerrar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Indicadores de beneficios */}
      <div className="mt-3 pt-3 border-t border-blue-200">
        <div className="flex items-center justify-between gap-4 text-xs text-blue-600 flex-wrap">
          <div className="flex items-center space-x-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span>Más rápido</span>
          </div>
          <div className="flex items-center space-x-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5z" />
            </svg>
            <span>Offline</span>
          </div>
          <div className="flex items-center space-x-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5z" />
            </svg>
            <span>Notificaciones</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente específico para el perfil
export const ProfilePWASuggestion: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <PWASuggestion 
      className={className} 
      showInProfile={true} 
    />
  );
};

// Componente para header/navbar
export const HeaderPWASuggestion: React.FC<{ className?: string }> = ({ className = '' }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isInStandalone, setIsInStandalone] = useState(false);

  useEffect(() => {
    // Detectar si ya está en modo standalone (PWA)
    const checkStandalone = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                        (window.navigator as any).standalone === true;
      setIsInStandalone(standalone);
    };

    checkStandalone();
    window.addEventListener('resize', checkStandalone);

    // Verificar si ya fue descartada anteriormente
    const dismissed = localStorage.getItem('pwa-header-suggestion-dismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
      setIsVisible(false);
    }

    return () => {
      window.removeEventListener('resize', checkStandalone);
    };
  }, []);

  // Solo mostrar en header si no está en modo standalone y no ha sido descartada
  if (isInStandalone || !isVisible || isDismissed) return null;

  const handleDismiss = () => {
    setIsDismissed(true);
    setIsVisible(false);
    localStorage.setItem('pwa-header-suggestion-dismissed', 'true');
  };

  return (
    <div className={`bg-blue-50 border-b border-blue-200 px-4 py-2 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <span className="text-sm text-blue-700">
            Instala la app para mejor experiencia
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => window.location.href = '/pwa-info'}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium focus:outline-none transition-colors"
          >
            Instalar →
          </button>
          <button
            onClick={handleDismiss}
            className="text-blue-400 hover:text-blue-600 focus:outline-none transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
